import { Handshake, TrendingUp, Users, ShieldCheck, Banknote, MapPin, ArrowUpRight, Percent, Headset, Truck, LineChart, Phone, Download } from 'lucide-react';
import { Link } from 'react-router-dom';
import { brand, image } from '../lib/siteData';
import { PageHero, Reveal, Eyebrow, WhatsAppButton, ProcessSteps, TextLink } from '../components/Shared';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';
import { FranchiseEnquiryForm } from '../components/FranchiseEnquiryForm';

const investmentStats = [
  { label: 'Franchise Fee (1 Equipment Kit)', value: '₹1.99 Lakh + GST' },
  { label: 'Potential Monthly Earnings*', value: '₹40,000 – ₹60,000' },
];

const campaignTags = ['Training', 'Equipment', 'Branding', 'Booking Support', 'Business Setup Support'];
const campaignBannerPoints = ['Low Investment', 'Complete Setup & Training', 'Trusted Local Brand'];

const highlights = [
  { icon: Banknote, label: 'Affordable Entry Investment', text: 'Start a doorstep vehicle & home-care business at ₹1.99 Lakh + GST — one complete equipment kit included, with extra kits available at ₹59,000 each as you grow.' },
  { icon: ShieldCheck, label: 'A Name Customers Trust', text: 'Operate under a brand already known in Bihar for quality, reliability and honest pricing.' },
  { icon: Users, label: 'Training & Onboarding', text: 'Your technicians are trained on our processes, products and customer standards before launch.' },
  { icon: TrendingUp, label: 'A Growing Category', text: 'Doorstep auto care is a growing need — cars, bikes, ACs, trucks, JCBs and water tanks, all in one brand.' },
];

const eligibility = [
  ['Local Market Knowledge', 'You understand your city or town and the vehicle owners in it.'],
  ['Ready to Invest', 'Comfortable committing the working capital needed to set up your unit.'],
  ['Hands-On Involvement', 'Willing to actively manage your team and day-to-day operations, at least in the early months.'],
  ['A Team-First Mindset', 'Able to hire, train and retain a small crew of technicians.'],
  ['Long-Term Outlook', 'Building a local service business takes a few months to find its rhythm — patience matters.'],
];

const support = [
  ['Equipment & Machines', 'A ready operating kit suited to the doorstep service model, shared in full detail before you commit.'],
  ['Training & Setup', 'Service-process, equipment-handling, customer-experience and pricing training before you launch.'],
  ['Branding & Marketing', 'Use of the Znta name and logo, launch creatives and promotional templates for your territory.'],
  ['Booking & Operational Support', 'A defined booking workflow, service checklists and day-to-day operating guidance.'],
  ['Ongoing Guidance', 'A point of contact at Znta for questions as your business gets going.'],
];

const rollout = [
  { wave: 'Wave 1', cities: ['Patna', 'Gaya', 'Bihar Sharif', 'Muzaffarpur'] },
  { wave: 'Wave 2', cities: ['Ara', 'Hajipur', 'Begusarai', 'Darbhanga'] },
  { wave: 'Wave 3', cities: ['Bhagalpur', 'Purnia', 'Chhapra', 'Samastipur'] },
];

const economics = [
  ['Jobs per operating day', '6', '8'],
  ['Average order value', '₹500', '₹550'],
  ['Operating days / month', '26', '26'],
  ['Illustrative monthly revenue', '₹78,000', '₹1,14,400'],
  ['Indicative operating costs', '₹38,000', '₹54,400'],
  ['Illustrative owner surplus', '₹40,000', '₹60,000'],
];

const pricing = [
  ['Franchise Fee (includes 1 equipment kit)', '₹1.99 Lakh + GST'],
  ['Additional Equipment Kit (optional)', '₹59,000 each'],
];

const faqs = [
  ['Is the ₹40,000–₹60,000 figure guaranteed?', 'No. It is an illustrative range built on sample assumptions — jobs per day, average order value and running costs. Your actual numbers will depend on your bookings, pricing, local demand and how the unit is run.'],
  ['What exactly is included in the ₹1.99 Lakh franchise fee?', 'One complete equipment kit, training & setup, branding & marketing material, and booking & operational support, plus applicable GST. Need to run more than one team? Additional equipment kits are available at ₹59,000 each. A full written item list and fee breakdown is shared before any payment.'],
  ['Will I get an exclusive territory?', 'Territory terms are confirmed individually for each partner during onboarding, based on your city and coverage area.'],
  ['Do I need prior experience in this industry?', 'No. We train you and your team on our processes and service standards — a willingness to learn and manage a small team matters more than prior experience.'],
  ['Do I need a shop or workshop to start?', 'No large workshop is required — this is a doorstep-first model. A small base to store equipment and products is enough for most categories.'],
  ['Can I run it part-time or through a manager?', 'A unit can be manager-operated, but reliable daily supervision, fast booking response and quality control matter a great deal, especially in the first few months.'],
  ['How soon can I get started?', 'Timelines depend on documentation, payment, equipment delivery, training and your city\u2019s readiness. Share your details on WhatsApp and our team will map out a realistic plan for your location.'],
];

const franchiseMessage = "Hi Znta Auto Spa, I want franchise details for my city. Please share the investment breakdown and next steps.";

export default function Franchise() {
  return <>
    <PageHero chapter="06 / PARTNER WITH US" title="BRING Znta" accent="TO YOUR CITY." description="Join us as a franchise partner and build a doorstep vehicle & home care business backed by a trusted brand, trained processes and real local demand."/>

    <section className="franchise-campaign">
      <img src={image('pressure_wash_man')} alt="Znta Auto Spa technician at work" loading="lazy"/>
      <div className="franchise-campaign-overlay"/>
      <div className="wrap franchise-campaign-content">
        <Reveal>
          <span className="franchise-offer-pill" data-testid="franchise-limited-badge"><i/>Franchise Opportunity<i/></span>
          <h2 className="franchise-campaign-headline">DOORSTEP CARWASH<br/><em>FRANCHISE</em></h2>
          <p className="franchise-campaign-sub" lang="hi-Latn">Apne Shehar Mein Shuru Karein <strong>Znta™ Auto Spa</strong> — a doorstep vehicle &amp; home-care business, built on a brand people already trust.</p>
          <div className="franchise-offer-boxes" data-testid="franchise-stats">
            {investmentStats.map((s, i) => <div key={s.label} className={i === 0 ? 'invest' : 'earn'}><span className="mono">{s.label}</span><strong>{s.value}</strong></div>)}
          </div>
          <div className="franchise-banner-strip">{campaignBannerPoints.map(t => <span key={t}>{t}</span>)}</div>
          <div className="franchise-tags">{campaignTags.map(t => <span key={t}>{t}</span>)}</div>
          <div className="franchise-cta-actions">
            <WhatsAppButton id="franchise-hero-whatsapp" message={franchiseMessage}>Enquire on WhatsApp</WhatsAppButton>
            <a className="text-link light" href="/documents/znta_francise.pdf" download="znta_francise.pdf" data-testid="franchise-hero-pdf-download"><Download size={16}/>Download Franchise Brochure (PDF)</a>
          </div>
          <div className="franchise-contact-strip">
            <a href={brand.tel} data-testid="franchise-hero-phone"><Phone size={15}/>{brand.phone}</a>
            <a href={brand.website} target="_blank" rel="noopener noreferrer" data-testid="franchise-hero-website"><ArrowUpRight size={14}/>{brand.website?.replace('https://', '')}</a>
          </div>
          <p className="franchise-disclaimer">*Illustrative potential only. Actual revenue depends on bookings, pricing, operating costs, local demand and execution. No income is guaranteed.</p>
        </Reveal>
      </div>
    </section>

    <section className="wrap about-story">
      <Reveal className="about-story-copy">
        <Eyebrow>A BUSINESS BUILT ON CARE</Eyebrow>
        <h2>YOUR CITY.<br/>OUR STANDARD.<br/><em>YOUR BUSINESS.</em></h2>
        <p>Znta Auto Spa started with a simple idea: care beyond the shine, delivered right at the doorstep. As demand grows across cars, bikes, ACs, water tanks and heavy vehicles, we're looking for partners who want to bring that same standard of care to their own city.</p>
        <p>As a franchise partner, you get a proven service model, hands-on training and a brand customers already recognise — so you can focus on running a great local business.</p>
      </Reveal>
      <Reveal className="franchise-highlights" delay={0.08}>
        {highlights.map(h => <div key={h.label} className="franchise-highlight-item">
          <h.icon size={24}/>
          <div><h3>{h.label}</h3><p>{h.text}</p></div>
        </div>)}
      </Reveal>
    </section>

    <section className="wrap franchise-economics section-space" id="franchise-pricing">
      <Reveal><Eyebrow>FRANCHISE MODEL & PRICING</Eyebrow><h2>ONE KIT TO START. <em>SCALE WHEN YOU'RE READY.</em></h2><p className="section-description">Your franchise fee includes one complete equipment kit — enough to run a single service team from day one. Add more kits any time to run additional teams and take on more business.</p></Reveal>
      <Reveal delay={0.06} className="franchise-economics-table">
        <table>
          <thead><tr><th>What you pay for</th><th>Price</th></tr></thead>
          <tbody>{pricing.map(([label, price]) => <tr key={label}><td>{label}</td><td>{price}</td></tr>)}</tbody>
        </table>
      </Reveal>
      <p className="franchise-disclaimer" data-testid="franchise-pricing-disclaimer">*Additional kits are optional and only needed if you want to run more than one service team under your franchise. GST and any other applicable charges will be confirmed in your written fee sheet before payment.</p>
    </section>

    <section className="wrap franchise-framework section-space">
      <Reveal><Eyebrow>FRANCHISE FRAMEWORK</Eyebrow><h2>WHAT IT TAKES. <em>WHAT YOU GET.</em></h2><p className="section-description">A look at what we expect from a partner, and what Znta brings to the table in return.</p></Reveal>
      <Tabs defaultValue="eligibility" className="franchise-tabs">
        <TabsList>
          <TabsTrigger value="eligibility" data-testid="franchise-tab-eligibility">Partner Eligibility</TabsTrigger>
          <TabsTrigger value="support" data-testid="franchise-tab-support">Brand Support</TabsTrigger>
        </TabsList>
        <TabsContent value="eligibility"><div className="franchise-list">{eligibility.map(([title, text], i) => <div key={title} className="franchise-list-item"><span className="mono">0{i+1}</span><div><h3>{title}</h3><p>{text}</p></div></div>)}</div></TabsContent>
        <TabsContent value="support"><div className="franchise-list">{support.map(([title, text], i) => <div key={title} className="franchise-list-item"><span className="mono">0{i+1}</span><div><h3>{title}</h3><p>{text}</p></div></div>)}</div></TabsContent>
      </Tabs>
    </section>

    <section className="franchise-process">
      <div className="wrap section-space">
        <Eyebrow>HOW IT WORKS</Eyebrow>
        <h2>THREE STEPS TO <em>YOUR OWN Znta.</em></h2>
        <ProcessSteps steps={[['Reach out', 'Share your city and a little about yourself on WhatsApp or by phone.'], ['Discuss the model', 'Our team walks you through investment, territory and support in detail.'], ['Set up & launch', 'We help you onboard your team and get your first bookings rolling.']]}/>
      </div>
    </section>

    <section className="wrap franchise-why section-space">
      <Reveal><Eyebrow>WHY PARTNER WITH Znta</Eyebrow><h2>BUILT FOR A <em>LOCAL PARTNER.</em></h2></Reveal>
      <div className="franchise-why-grid">
        <Reveal delay={0.04}><Percent size={22}/><h3>Clear Terms</h3><p>Straightforward franchise terms discussed upfront — no hidden surprises.</p></Reveal>
        <Reveal delay={0.08}><Headset size={22}/><h3>A Real Point of Contact</h3><p>Direct access to our team for operational questions as you get going.</p></Reveal>
        <Reveal delay={0.12}><Truck size={22}/><h3>Multi-Category Model</h3><p>One brand covering cars, bikes, ACs, trucks, JCBs and water tanks — more ways to earn.</p></Reveal>
        <Reveal delay={0.16}><LineChart size={22}/><h3>Room to Grow</h3><p>Start with your core services and expand into more categories as your unit matures.</p></Reveal>
      </div>
    </section>

    <section className="wrap franchise-economics section-space">
      <Reveal><Eyebrow>A SAMPLE UNIT ECONOMICS VIEW</Eyebrow><h2>WHAT THE NUMBERS <em>COULD LOOK LIKE.</em></h2><p className="section-description">An illustrative model only — not a promise. Replace these assumptions with real numbers for your city before deciding.</p></Reveal>
      <Reveal delay={0.06} className="franchise-economics-table">
        <table>
          <thead><tr><th>Illustrative input</th><th>Low case</th><th>High case</th></tr></thead>
          <tbody>{economics.map(([label, low, high]) => <tr key={label}><td>{label}</td><td>{low}</td><td>{high}</td></tr>)}</tbody>
        </table>
      </Reveal>
      <p className="franchise-disclaimer" data-testid="franchise-economics-disclaimer">*Illustrative only. Excludes rent, insurance, financing costs and taxes where applicable — always plan for your own real costs before committing.</p>
    </section>

    <section className="wrap franchise-rollout section-space">
      <Reveal><Eyebrow>WHERE WE'RE EXPANDING</Eyebrow><h2>BIHAR CITY <em>ROLLOUT.</em></h2><p className="section-description">We're opening franchise conversations city by city, starting close to home. {brand.cities.join(', ')} are where Znta already operates today.</p></Reveal>
      <div className="franchise-rollout-grid">
        {rollout.map((r, i) => <Reveal key={r.wave} delay={i * 0.06} className="franchise-rollout-card">
          <span className="mono">{r.wave}</span>
          <p>{r.cities.join(' · ')}</p>
        </Reveal>)}
      </div>
    </section>

    <section className="wrap franchise-faq section-space">
      <Reveal><Eyebrow>GOT QUESTIONS?</Eyebrow><h2>FREQUENTLY ASKED <em>QUESTIONS.</em></h2></Reveal>
      <Reveal delay={0.06}>
        <Accordion type="single" collapsible className="franchise-accordion">
          {faqs.map(([q, a], i) => <AccordionItem key={q} value={`faq-${i}`} data-testid={`franchise-faq-${i}`}><AccordionTrigger>{q}</AccordionTrigger><AccordionContent><p>{a}</p></AccordionContent></AccordionItem>)}
        </Accordion>
      </Reveal>
    </section>

    <section className="franchise-enquiry-section wrap section-space" id="franchise-enquire">
      <Reveal className="franchise-enquiry-copy">
        <Handshake size={28}/>
        <Eyebrow>LET'S BUILD SOMETHING TOGETHER</Eyebrow>
        <h2>READY TO BECOME A<br/><em>Znta PARTNER?</em></h2>
        <p>Share a few details and we'll get back to you on WhatsApp with investment, territory and support information for your city.</p>
        <div className="franchise-cities" data-testid="franchise-current-cities">
          <span className="mono">CURRENTLY SERVING</span>
          <p>{brand.cities.map((city, i) => <span key={city}><MapPin size={13}/>{city}{i < brand.cities.length - 1 ? ' · ' : ''}</span>)}</p>
        </div>
        <a className="text-link" href="/documents/znta_francise.pdf" download="znta_francise.pdf" data-testid="franchise-enquiry-pdf-download"><Download size={19}/>Download the full franchise playbook (PDF)</a>
        <Link className="text-link" to="/referral" data-testid="franchise-see-referral"><ArrowUpRight size={19}/>Refer someone and earn a commission</Link>
        <TextLink to="/contact" id="franchise-general-contact">Have a different question? Contact us</TextLink>
      </Reveal>
      <Reveal delay={0.1} className="form-panel">
        <div className="form-heading"><h3>Tell us about your city.</h3><span className="mono">FRANCHISE — ENQUIRY</span></div>
        <FranchiseEnquiryForm/>
      </Reveal>
    </section>
  </>;
}
